import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toEvidenceView } from '../scenes/OpsBriefing/adapters';
import { decideOpsBriefingAccess, AccessDecision } from '../scenes/OpsBriefing/accessGate';
import {
    getOpsBriefingAccessAPI,
    getOpsBriefingAPI,
    getOpsEvidenceAPI,
    getOpsMembersAPI,
    getOpsCoachesAPI,
    getOpsOrdersAPI,
    createOpsOrderAPI,
    updateOpsOrderAPI,
    retractOpsOrderAPI,
    getOpsHistoryAPI,
    setHistoryItemDoneAPI,
    setOpsBriefingItemStatusAPI,
    setOpsMemberHiddenAPI,
    setOpsCoachHiddenAPI,
    getOpsOnboardingAPI,
    getOpsCoachSettlingAPI,
    getOpsCoachOnboardingAPI,
} from '../api/opsBriefing';
import { OnboardingPayload } from '../scenes/OpsBriefing/onboarding';
import { CoachSettlingPayload } from '../scenes/OpsBriefing/coachSettling';
import { CoachOnboardingBriefing } from '../types/opsBriefing';
import {
    OpsBriefingAccessResponse,
    OpsBriefingResponse,
    EvidenceResponse,
    MembersResponse,
    CoachesResponse,
    HistoryResponse,
    OrderDto,
    CreateOrderRequest,
    UpdateOrderRequest,
    UpdateBriefingItemStatusRequest,
    RetractOrderRequest,
    DateKst,
} from '../types/opsBriefing';

/**
 * 데일리 운영 브리핑 조회·변경 훅.
 *
 * useAdminAlertsService 와 같은 재시도·캐시 정책을 씁니다.
 * 브리핑은 하루 한 번 생성되므로 자주 다시 부를 이유가 없고,
 * 대신 지시(order)는 강사가 실행하면 바뀌므로 짧게 잡습니다.
 */

export const OPS_BRIEFING_KEY = 'opsBriefing';

export const opsBriefingKeys = {
    access: (centerId: string) => [OPS_BRIEFING_KEY, 'access', centerId] as const,
    briefing: (centerId: string, date: DateKst) => [OPS_BRIEFING_KEY, 'today', centerId, date] as const,
    evidence: (centerId: string, itemId: string) => [OPS_BRIEFING_KEY, 'evidence', centerId, itemId] as const,
    members: (centerId: string, date: DateKst) => [OPS_BRIEFING_KEY, 'members', centerId, date] as const,
    coaches: (centerId: string, month: string) => [OPS_BRIEFING_KEY, 'coaches', centerId, month] as const,
    orders: (centerId: string) => [OPS_BRIEFING_KEY, 'orders', centerId] as const,
    /**
     * 철회한 지시 — 같은 엔드포인트지만 **다른 요청**이라 키를 나눕니다.
     * 같은 키를 쓰면 둘 중 나중에 온 응답이 상대를 덮어써서, 진행 중 목록에
     * 정리된 건이 섞이거나 그 반대가 됩니다.
     */
    retractedOrders: (centerId: string) =>
        [OPS_BRIEFING_KEY, 'orders', centerId, 'retracted'] as const,
    /**
     * 그 달 지시 전부(정리된 건 포함) — 강사 리포트 전용.
     * 진행 중 목록과 **다른 요청**이라 키를 나눕니다(위 retractedOrders 와 같은 이유).
     */
    monthOrders: (centerId: string, month: string) =>
        [OPS_BRIEFING_KEY, 'orders', centerId, 'month', month] as const,
    history: (centerId: string, month: string) => [OPS_BRIEFING_KEY, 'history', centerId, month] as const,
    /**
     * 세팅 집계 2종 — 날짜 축이 없습니다.
     * 서버가 `?date=`·`?month=` 를 아예 안 받습니다(현재 상태 집계라 그렇습니다).
     * 키에 날짜를 넣으면 날짜를 옮길 때마다 같은 값을 다시 받습니다.
     */
    onboarding: (centerId: string) => [OPS_BRIEFING_KEY, 'onboarding', centerId] as const,
    coachSettling: (centerId: string) => [OPS_BRIEFING_KEY, 'coachSettling', centerId] as const,
    coachOnboarding: (centerId: string) =>
        [OPS_BRIEFING_KEY, 'coachOnboarding', centerId] as const,
};

const getErrorStatus = (error: unknown): number | undefined => {
    const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
    return typeof status === 'number' ? status : undefined;
};

/** 4xx 는 재시도해도 같은 답이 온다. 5xx·네트워크만 한 번 더 */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
    if (failureCount >= 1) return false;
    const status = getErrorStatus(error);
    if (status !== undefined && status >= 400 && status < 500) return false;
    return true;
};

const retryWithBackoff = (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 8000);

/** 브리핑은 하루 한 번 생성되므로 5분은 다시 안 불러도 된다 */
const DAILY_STALE_MS = 5 * 60 * 1000;
/** 지시는 강사가 실행하면 바뀐다. 30초면 충분히 최신이다 */
const ORDER_STALE_MS = 30 * 1000;
/**
 * 철회 전달 대기 건이 있을 때만 다시 묻는 주기.
 *
 * 서버 배치가 10분마다 도니 그보다 촘촘할 이유가 없고, 그보다 성기면
 * 원장이 이미 화면을 닫은 뒤에 결과가 바뀝니다. 1분으로 둡니다 —
 * 배치가 성공한 직후를 늦어도 1분 안에 따라잡습니다.
 */
const RETRACT_POLL_MS = 60 * 1000;
/**
 * 노출 여부는 서버 환경변수라 배포 전에는 바뀌지 않는다.
 * useTeamFeatureStatus 와 같은 5분 — 화면을 옮길 때마다 다시 물으면
 * GNB 가 매번 판정 전(메뉴 없음)으로 돌아가 메뉴가 깜빡인다.
 */
const ACCESS_STALE_MS = 5 * 60 * 1000;

type Ctx = { accessToken: string; centerId: string };

const ready = (ctx: Ctx, enabled = true) => enabled && !!ctx.accessToken && !!ctx.centerId;

/* ── ⓪ 노출 여부 ─────────────────────────────────────────── */

/**
 * 이 센터에 브리핑이 열려 있는지 묻는다.
 *
 * 이 엔드포인트만 서버 노출 게이트 **밖**이라 닫힌 센터에서도 200 이 온다.
 * 나머지 9개는 게이트 안이라 닫힌 센터에서 부르면 404 다 —
 * 그래서 다른 조회는 전부 이 값이 true 로 확정된 뒤에 켠다.
 */
export const useOpsBriefingAccessQuery = (ctx: Ctx, enabled = true) =>
    useQuery<OpsBriefingAccessResponse>({
        queryKey: opsBriefingKeys.access(ctx.centerId),
        queryFn: () => getOpsBriefingAccessAPI(ctx),
        enabled: ready(ctx, enabled),
        staleTime: ACCESS_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/**
 * 화면이 실제로 쓰는 판정.
 *
 * ⚠️ `data?.enabled` 를 직접 보지 마세요. 로딩과 '닫힘'이 구분되지 않아
 *    열린 센터의 원장이 첫 렌더마다 대시보드로 튕겨나갑니다.
 *    판정 규칙은 accessGate.ts 에 있고 테스트가 붙어 있습니다.
 */
export const useOpsBriefingAccess = (ctx: Ctx): AccessDecision => {
    const query = useOpsBriefingAccessQuery(ctx);
    return decideOpsBriefingAccess(
        { data: query.data, isError: query.isError },
        ready(ctx)
    );
};

/* ── ① 오늘의 브리핑 ─────────────────────────────────────── */

export const useOpsBriefingQuery = (ctx: Ctx, date: DateKst, enabled = true) =>
    useQuery<OpsBriefingResponse>({
        queryKey: opsBriefingKeys.briefing(ctx.centerId, date),
        queryFn: () => getOpsBriefingAPI(ctx, date),
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/* ── ② 원천 데이터 ───────────────────────────────────────── */

export const useOpsEvidenceQuery = (ctx: Ctx, itemId: string | null) =>
    useQuery<EvidenceResponse | null>({
        queryKey: opsBriefingKeys.evidence(ctx.centerId, itemId ?? ''),
        // 순번 근거는 프론트가 붙인다 (adapters.toEvidenceView 주석 참고)
        queryFn: () =>
            getOpsEvidenceAPI(ctx, itemId as string).then((res) =>
                res ? toEvidenceView(res) : null
            ),
        // 근거 팝업을 열 때만 부른다. 미리 다 받아오면 낭비다.
        enabled: ready(ctx) && !!itemId,
        staleTime: DAILY_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/* ── ③ 회원 현황 ─────────────────────────────────────────── */

export const useOpsMembersQuery = (ctx: Ctx, date: DateKst, enabled = true) =>
    useQuery<MembersResponse>({
        queryKey: opsBriefingKeys.members(ctx.centerId, date),
        queryFn: () => getOpsMembersAPI(ctx, date),
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/**
 * 회원 한 명을 회원 현황에서 가리거나 다시 보이게 한다.
 *
 * ─────────────────────────────────────────────────────────────
 * 낙관 반영은 **지금 보고 있는 날짜**에 하고, 확정은 전체 날짜에 한다
 * ─────────────────────────────────────────────────────────────
 * 회원 가리기는 날짜 축이 없습니다(다시 보기 전까지 영구). 그런데 회원 현황
 * 캐시는 날짜별로 나뉘어 있어서, 어제 날짜를 열어 두고 가린 뒤 오늘로 돌아오면
 * 그쪽 캐시에는 옛 `hidden` 이 남습니다. 그래서
 *   · 즉시 반응은 지금 보고 있는 `date` 캐시에만 (되돌릴 대상이 하나여야 한다)
 *   · 확정된 뒤에는 이 센터의 **모든 날짜** 회원 현황을 무효화
 * 로 나눕니다.
 *
 * ⚠️ 실패하면 반드시 되돌립니다. 서버에 아직 이 엔드포인트가 없으면(배포 전 404)
 *    화면만 가려진 채로 남고 새로고침에 되돌아옵니다 — "가렸습니다" 라고 말해
 *    놓고 되돌아오는 것이 이 기능에서 가장 나쁩니다. 호출부는 성공 콜백에서만
 *    성공 문구를 띄웁니다.
 *
 * ⚠️ 요청 중에는 호출부가 그 회원의 버튼을 잠가야 합니다. 서버는 반대 방향
 *    동시 요청(가리기 ↔ 다시 보기)에서 **마지막에 실행된 write 가 이기고**
 *    제출 순서를 보장하지 않습니다.
 */
export const useSetOpsMemberHiddenMutation = (ctx: Ctx, date: DateKst) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, hidden }: { memberId: string; hidden: boolean }) =>
            setOpsMemberHiddenAPI(ctx, memberId, hidden),
        onMutate: async ({ memberId, hidden }) => {
            const key = opsBriefingKeys.members(ctx.centerId, date);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<MembersResponse>(key);
            if (prev) {
                qc.setQueryData<MembersResponse>(key, {
                    ...prev,
                    /*
                     * 목록에서 **빼지 않고 hidden 만 바꿉니다.**
                     * 빼면 "가린 회원 N명 보기"에 그 회원이 안 나타나 되돌릴 길이
                     * 사라지고, 서버도 가려진 회원을 응답에서 빼지 않습니다.
                     * 표에서 감추는 것은 화면의 일입니다.
                     */
                    members: prev.members.map((m) =>
                        m.memberId === memberId ? { ...m, hidden } : m
                    ),
                });
            }
            return { prev };
        },
        onError: (_e, _v, context) => {
            const key = opsBriefingKeys.members(ctx.centerId, date);
            if (context?.prev) qc.setQueryData(key, context.prev);
        },
        onSettled: () => {
            /*
             * 날짜를 지정하지 않습니다 — 가림은 날짜와 무관하게 적용되므로
             * 다른 날짜 캐시에 남은 옛 hidden 도 같이 털어야 합니다.
             * (키가 [ops, 'members', centerId, date] 라 앞 세 칸이 접두사입니다)
             */
            qc.invalidateQueries({ queryKey: [OPS_BRIEFING_KEY, 'members', ctx.centerId] });
        },
    });
};

/* ── ④ 강사 현황 ─────────────────────────────────────────── */

export const useOpsCoachesQuery = (ctx: Ctx, month: string, enabled = true) =>
    useQuery<CoachesResponse>({
        queryKey: opsBriefingKeys.coaches(ctx.centerId, month),
        queryFn: () => getOpsCoachesAPI(ctx, month),
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/**
 * 강사 한 명을 강사 현황에서 가리거나 다시 보이게 한다.
 *
 * ─────────────────────────────────────────────────────────────
 * 회원 가리기(useSetOpsMemberHiddenMutation)와 **같은 구조**입니다
 * ─────────────────────────────────────────────────────────────
 * 낙관 반영은 **지금 보고 있는 달**에만, 확정 뒤 무효화는 **모든 달**에.
 * 강사 가리기도 월 축이 없는데(다시 보기 전까지 영구) 강사 현황 캐시는
 * 월별로 나뉘어 있어서, 지난달을 열어 두고 가린 뒤 이번 달로 돌아오면
 * 그쪽 캐시에 옛 `hidden` 이 남습니다.
 *
 * ⚠️ 목록에서 **빼지 않고 `hidden` 만 바꿉니다.** 빼면 "가린 강사 N명 보기"에
 *    그 강사가 안 나타나 되돌릴 길이 사라집니다. 서버도 가려진 강사를
 *    응답에서 빼지 않습니다.
 *
 * ⚠️ `closing`(하단 지표 표)도 **함께** 뒤집습니다. 한쪽만 바꾸면 강사 목록에서는
 *    사라진 강사가 표에는 그대로 남아, 원장이 "가렸는데 아직 있다"고 읽습니다.
 *
 * ⚠️ 실패하면 반드시 되돌립니다. 서버에 아직 이 엔드포인트가 없으면(배포 전 404)
 *    화면만 가려진 채로 남고 새로고침에 되돌아옵니다. 호출부는 성공 콜백에서만
 *    성공 문구를 띄웁니다.
 */
export const useSetOpsCoachHiddenMutation = (ctx: Ctx, month: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ coachId, hidden }: { coachId: string; hidden: boolean }) =>
            setOpsCoachHiddenAPI(ctx, coachId, hidden),
        onMutate: async ({ coachId, hidden }) => {
            const key = opsBriefingKeys.coaches(ctx.centerId, month);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<CoachesResponse>(key);
            if (prev) {
                qc.setQueryData<CoachesResponse>(key, {
                    ...prev,
                    coaches: prev.coaches.map((c) =>
                        c.coachId === coachId ? { ...c, hidden } : c
                    ),
                    /* 클로징 표도 같이 — 두 목록이 갈리면 표에만 남는다 */
                    closing: prev.closing.map((c) =>
                        c.coachId === coachId ? { ...c, hidden } : c
                    ),
                });
            }
            return { prev };
        },
        onError: (_e, _v, context) => {
            const key = opsBriefingKeys.coaches(ctx.centerId, month);
            if (context?.prev) qc.setQueryData(key, context.prev);
        },
        onSettled: () => {
            /*
             * 달을 지정하지 않습니다 — 가림은 달과 무관하게 적용되므로
             * 다른 달 캐시에 남은 옛 hidden 도 같이 털어야 합니다.
             * (키가 [ops, 'coaches', centerId, month] 라 앞 세 칸이 접두사입니다)
             */
            qc.invalidateQueries({ queryKey: [OPS_BRIEFING_KEY, 'coaches', ctx.centerId] });
        },
    });
};

/* ── ⑤ 지시 ──────────────────────────────────────────────── */

export const useOpsOrdersQuery = (ctx: Ctx, enabled = true) =>
    useQuery<OrderDto[]>({
        queryKey: opsBriefingKeys.orders(ctx.centerId),
        queryFn: () => getOpsOrdersAPI(ctx),
        enabled: ready(ctx, enabled),
        staleTime: ORDER_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/**
 * 그 달에 나간 지시 **전부** — 강사 리포트의 이행률용.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 기본 목록을 못 쓰나 — 이행률이 틀려 있었습니다
 * ─────────────────────────────────────────────────────────────
 * 리포트는 `ARCHIVED`(정리됨)를 **완료로 셉니다**(`reportComposer.isDone`).
 * 그런데 기본 목록은 `ARCHIVED` 를 **빼고** 줍니다. 그래서 원장이 정리해 둔
 * 지시가 분자·분모에서 통째로 사라지고, 월 이행률이 실제보다 낮게 나왔습니다 —
 * 강사에게 건네는 종이에 「확인율 62%」라고 찍히는 자리입니다.
 *
 * `month` 로 그 달만 받으므로 진행 중 목록의 200건 상한 문제도 안 겪습니다.
 * (상한은 필터 뒤에도 남지만, 한 달치라 훨씬 여유롭습니다. 그래도 넘칠 수
 *  있으므로 리포트가 캡션으로 밝힙니다)
 *
 * ⚠️ 이 훅을 진행 중 목록에 쓰지 마세요. 정리된 건이 섞여 「아직 안 한 일」이
 *    부풀어 보입니다.
 */
export const useOpsOrdersMonthQuery = (ctx: Ctx, month: string, enabled = true) =>
    useQuery<OrderDto[]>({
        queryKey: opsBriefingKeys.monthOrders(ctx.centerId, month),
        queryFn: () => getOpsOrdersAPI(ctx, { includeArchived: true, month }),
        enabled: ready(ctx, enabled) && !!month,
        staleTime: ORDER_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

/**
 * 철회한 지시.
 *
 * 기본 목록에서 빠지므로(`ARCHIVED`·`RETRACTED` 둘 다) 따로 부릅니다.
 * 원장이 "내가 뭘 취소했더라"를 확인할 통로가 없으면, 철회는 눌린 뒤 흔적 없이
 * 사라지는 동작이 됩니다. 전달 실패(FAILED)는 원장이 **직접 연락해야 하는**
 * 상태라 더더욱 화면에 떠 있어야 합니다.
 *
 * `onlyRetracted` 는 `includeArchived` 의 좁은 버전이 아니라 **정렬이 다릅니다.**
 * 서버가 이 옵션에서만 `retractedAt` 내림차순으로 줍니다. `sentAt` 으로 자르면
 * 오래된 지시를 오늘 철회했을 때 상한 창 밖으로 밀리고, 그게 하필 전달
 * 실패(FAILED)면 원장은 강사가 철회를 모른다는 사실을 영영 못 봅니다.
 * 강사 목록도 같은 이유로 `retractedAt` 으로 정렬합니다.
 *
 * ⚠️ 진행 중 목록을 이 옵션으로 대신 부르지 않습니다. 서버 상한이 있어
 *    두 목록이 창을 나눠 쓰면, 아직 처리 안 된 오래된 지시가 밀려납니다.
 *    요청 하나를 아끼려다 밀린 지시를 잃습니다.
 */
export const useOpsRetractedOrdersQuery = (ctx: Ctx, enabled = true) =>
    useQuery<OrderDto[]>({
        queryKey: opsBriefingKeys.retractedOrders(ctx.centerId),
        queryFn: () => getOpsOrdersAPI(ctx, { onlyRetracted: true }),
        enabled: ready(ctx, enabled),
        staleTime: ORDER_STALE_MS,
        /**
         * 전달 대기 중인 철회가 있으면 **주기적으로 다시 묻습니다.**
         *
         * 이게 없으면 화면을 열어 둔 원장은 `PENDING` 을 몇 시간이고 `PENDING`
         * 으로 봅니다. `staleTime` 은 다시 부르는 타이머가 아니라 "이 시간 안에는
         * 다시 안 불러도 된다"는 상한일 뿐이고, 이 앱은 전역 설정에서 창 포커스
         * 재조회도 꺼 두었습니다(index.tsx). 즉 **아무도 다시 묻지 않습니다.**
         *
         * 그러면 "FAILED 를 맨 위에 꺼낸다"는 방어가 통째로 헛것이 됩니다.
         * 서버는 10분마다 재시도하고 최대 6시간 뒤 FAILED 로 바꾸는데, 화면이
         * 그 전환을 영영 못 보면 원장은 강사가 취소된 지시를 실행하는 것을
         * 끝내 모릅니다.
         *
         * 대기 중인 것이 하나도 없으면 끕니다 — 확정된 상태는 다시 물을 이유가
         * 없고, 매일 아침 여는 화면에 쓸데없는 요청을 깔면 안 됩니다.
         */
        refetchInterval: (data) =>
            data?.some((o) => o.retractDelivery === 'PENDING') ? RETRACT_POLL_MS : false,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

export const useCreateOpsOrderMutation = (ctx: Ctx) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateOrderRequest) => createOpsOrderAPI(ctx, body),
        /**
         * 낙관적 업데이트.
         *
         * 지시 생성은 왕복이 있어서, 누르고 나서 카드가 그대로 있으면
         * 원장님은 "안 눌렸나?" 하고 한 번 더 누릅니다. 지시 생성은 멱등이 아니라
         * 같은 회원에게 지시가 두 건 만들어집니다.
         * 버튼의 isLoading 가드가 1차 방어이고, 이 즉시 반영이 2차 방어입니다.
         *
         * ⚠️ 서버가 항목을 ACTED 로 바꾸는 것은 sourceItemId 와 sourceDate 가
         *    **둘 다** 있을 때뿐입니다(opsBriefing.service.createOrder).
         *    그래서 여기서도 둘 다 있을 때만 선반영합니다 —
         *    회원 현황 화면처럼 sourceItemId 없이 만든 지시로 브리핑 카드를
         *    완료 처리해 버리면, 원장이 아직 안 본 건이 조용히 사라집니다.
         */
        onMutate: async (body: CreateOrderRequest) => {
            if (!body.sourceItemId || !body.sourceDate) return { prev: undefined, key: undefined };
            const key = opsBriefingKeys.briefing(ctx.centerId, body.sourceDate);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<OpsBriefingResponse>(key);
            if (prev) {
                qc.setQueryData<OpsBriefingResponse>(key, {
                    ...prev,
                    items: prev.items.map((it) =>
                        it.itemId === body.sourceItemId ? { ...it, status: 'ACTED' } : it
                    ),
                });
            }
            return { prev, key };
        },
        onError: (_e, _v, context) => {
            // 실패했으면 카드를 원래대로 되돌린다. 안 되돌리면 안 나간 지시가 완료로 남는다.
            if (context?.key && context.prev) qc.setQueryData(context.key, context.prev);
        },
        onSettled: (_d, _e, body) => {
            qc.invalidateQueries({ queryKey: opsBriefingKeys.orders(ctx.centerId) });
            /*
             * sourceItemId 를 함께 보내면 서버가 그 브리핑 항목을 ACTED 로 바꿉니다
             * (opsBriefing.service.linkOrderToItem). 브리핑 캐시도 같이 털지 않으면
             * 지시는 생겼는데 카드가 계속 '미처리'로 남습니다.
             *
             * 성공·실패 어느 쪽이든 서버 상태로 맞춥니다 — 낙관 반영을 되돌린 뒤에도
             * 서버가 실제로 어떻게 됐는지는 다시 물어봐야 압니다.
             */
            if (body.sourceDate) {
                qc.invalidateQueries({
                    queryKey: opsBriefingKeys.briefing(ctx.centerId, body.sourceDate),
                });
            } else {
                qc.invalidateQueries({ queryKey: [OPS_BRIEFING_KEY, 'today'] });
            }
        },
    });
};

export const useUpdateOpsOrderMutation = (ctx: Ctx) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ orderId, body }: { orderId: string; body: UpdateOrderRequest }) =>
            updateOpsOrderAPI(ctx, orderId, body),
        /**
         * 낙관적 업데이트.
         * 체크박스를 눌렀는데 0.3초 뒤에 반응하면 두 번 누르게 된다.
         * 실패하면 이전 목록으로 되돌린다.
         */
        onMutate: async ({ orderId, body }) => {
            const key = opsBriefingKeys.orders(ctx.centerId);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<OrderDto[]>(key);
            if (prev) {
                qc.setQueryData<OrderDto[]>(
                    key,
                    prev.map((o) => (o.orderId === orderId ? { ...o, status: body.status } : o))
                );
            }
            return { prev };
        },
        onError: (_e, _v, context) => {
            const key = opsBriefingKeys.orders(ctx.centerId);
            if (context?.prev) qc.setQueryData(key, context.prev);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: opsBriefingKeys.orders(ctx.centerId) });
        },
    });
};

/**
 * 지시 철회 — "이건 잘못 보냈습니다".
 *
 * ─────────────────────────────────────────────────────────────
 * 낙관적 업데이트를 **일부러 하지 않습니다**
 * ─────────────────────────────────────────────────────────────
 * 다른 변경(완료 체크)은 결과가 뻔해서 미리 반영해도 됩니다. 철회는 다릅니다 —
 * 눌러 봐야 알 수 있는 것이 두 가지입니다.
 *
 *   · 회수인가 통보인가. 갈래는 서버가 저장된 사실(seenAt·coachVisibleAt)로
 *     정합니다. 화면의 seenAt 은 오래된 값일 수 있습니다.
 *   · 강사에게 닿았는가. 응답의 retractDelivery 로만 알 수 있습니다.
 *
 * 미리 "철회됨"으로 그려 놓으면 이 둘을 화면이 지어내게 됩니다. 서버가 400
 * (사유 필요)이나 409(이미 철회됨)로 거절해도 화면에는 이미 철회된 것처럼
 * 보입니다. 모르는 것을 말하지 않는 것이 이 기능 전체의 원칙입니다.
 *
 * 대신 화면은 확인 창을 연 채로 기다리고, 응답이 온 뒤에 목록을 다시 부릅니다.
 */
export const useRetractOpsOrderMutation = (ctx: Ctx) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ orderId, body }: { orderId: string; body: RetractOrderRequest }) =>
            retractOpsOrderAPI(ctx, orderId, body),
        /**
         * 성공 응답을 **곧바로 캐시에 씁니다.**
         *
         * ─────────────────────────────────────────────────────────────
         * 이건 낙관적 업데이트가 아닙니다
         * ─────────────────────────────────────────────────────────────
         * 낙관적 업데이트는 결과를 **추측**해서 미리 그리는 것이고, 그래서 위에
         * 안 하기로 했습니다. 이건 반대입니다 — 서버가 방금 돌려준 **확정된
         * 사실**을 쓰는 것입니다. 그 응답 안에 갈래(회수/통보)도 전달 상태도
         * 다 들어 있습니다.
         *
         * 무효화만 걸고 두면 응답이 돌아온 뒤에도 **다시 조회가 끝날 때까지**
         * 캐시에는 옛 객체가 남습니다. 그 몇백 밀리초 동안 화면은 이렇습니다.
         *   · "철회했습니다" 토스트가 떠 있는데 그 지시 행이 그대로 보이고
         *   · 진행률·금액에도 계속 잡히고
         *   · 철회 버튼도 그대로라 원장이 한 번 더 눌러 409 를 받습니다
         *
         * 화면이 방금 자기가 한 말과 다른 것을 보여주는 시간을 만들지 않습니다.
         */
        onSuccess: (updated) => {
            const activeKey = opsBriefingKeys.orders(ctx.centerId);
            const retractedKey = opsBriefingKeys.retractedOrders(ctx.centerId);

            /*
             * 진행 목록에서는 **지우지 않고 갈아끼웁니다.**
             * 지우면 "없던 일"이 되고, 갈아끼우면 화면의 RETRACTED 필터가
             * 알아서 뺍니다. 사실을 지우는 것과 상태를 고치는 것은 다릅니다.
             */
            qc.setQueryData<OrderDto[]>(activeKey, (prev) =>
                prev?.map((o) => (o.orderId === updated.orderId ? updated : o))
            );

            /*
             * 철회 목록에는 없으면 넣고 있으면 갈아끼웁니다.
             * 방금 철회한 건이 서랍에 안 보이면, 원장은 자기가 한 일이
             * 어디로 갔는지 알 수 없습니다.
             */
            qc.setQueryData<OrderDto[]>(retractedKey, (prev) => {
                if (!prev) return prev;
                return prev.some((o) => o.orderId === updated.orderId)
                    ? prev.map((o) => (o.orderId === updated.orderId ? updated : o))
                    : [updated, ...prev];
            });
        },
        /*
         * 그러고 나서 서버 상태로 한 번 더 맞춥니다.
         *
         * 위 쓰기는 이 한 건만 고칩니다. 그 사이에 강사가 다른 건을 완료했을
         * 수도 있고, 실패했을 때는 서버가 실제로 어떻게 됐는지 물어봐야 압니다.
         *
         * 철회 키가 orders 키를 접두사로 갖고 있어 한 번의 무효화로 둘 다 걸리지만,
         * 그 사실에 기대지 않고 명시합니다 — 나중에 키 모양이 바뀌면 조용히 한쪽만
         * 갱신되고, 방금 철회한 건이 진행 현황에 그대로 남습니다.
         */
        onSettled: () => {
            qc.invalidateQueries({ queryKey: opsBriefingKeys.orders(ctx.centerId) });
            qc.invalidateQueries({ queryKey: opsBriefingKeys.retractedOrders(ctx.centerId) });
        },
    });
};

/**
 * 오늘의 브리핑 항목을 완료/미완료로 기록한다.
 *
 * 서버 엔드포인트는 지난 리포트와 같습니다 —
 * PATCH .../history/:date/items/:index (opsBriefing.service.setItemDone).
 * 그쪽은 PUBLISHED 인 날이면 오늘이든 지난 날이든 똑같이 처리하므로
 * 오늘 화면도 같은 API 를 씁니다. 다만 무효화할 캐시가 다릅니다 —
 * 지난 리포트 훅을 그대로 쓰면 오늘 화면은 새로고침해야 반영됩니다.
 *
 * ⚠️ index 는 **서버가 내려준 items 배열의 위치**입니다.
 * 화면에서 항목을 걸러내거나 정렬을 바꾸면 다른 항목이 완료 처리됩니다.
 */
export const useSetOpsBriefingItemDoneMutation = (ctx: Ctx, date: DateKst) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ index, done }: { index: number; itemId: string; done: boolean }) =>
            setHistoryItemDoneAPI(ctx, date, index, done),
        /**
         * 낙관적 업데이트.
         * 체크박스가 0.3초 뒤에 반응하면 원장이 두 번 누릅니다.
         * 실패하면 이전 목록으로 되돌립니다.
         */
        onMutate: async ({ itemId, done }) => {
            const key = opsBriefingKeys.briefing(ctx.centerId, date);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<OpsBriefingResponse>(key);
            if (prev) {
                qc.setQueryData<OpsBriefingResponse>(key, {
                    ...prev,
                    items: prev.items.map((it) =>
                        it.itemId === itemId ? { ...it, status: done ? 'ACTED' : 'PENDING' } : it
                    ),
                });
            }
            return { prev };
        },
        onError: (_e, _v, context) => {
            const key = opsBriefingKeys.briefing(ctx.centerId, date);
            if (context?.prev) qc.setQueryData(key, context.prev);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: opsBriefingKeys.briefing(ctx.centerId, date) });
        },
    });
};

/**
 * 브리핑 항목을 가리거나(DISMISSED) 되돌린다(PENDING).
 *
 * ─────────────────────────────────────────────────────────────
 * 백필은 여기서 저절로 일어납니다
 * ─────────────────────────────────────────────────────────────
 * 낙관 반영으로 항목의 status 를 DISMISSED 로 바꾸면, 화면의 편성 함수
 * (briefingComposition.composeBriefing)가 그 항목을 미처리 목록에서 빼고
 * **다음 위험 순위를 그 자리에 올립니다.** 화면이 따로 "한 칸 당겨오는" 코드를
 * 갖지 않습니다 — 그런 코드는 새로고침 뒤 서버 상태와 어긋나는 순간
 * 목록이 조용히 달라집니다.
 *
 * 아침 팝업(MorningBriefingGate)도 **같은 쿼리 키**를 보므로 함께 반영됩니다.
 * 목록에서 가린 항목이 팝업에는 그대로 떠 있으면, 원장님은 가리기가
 * 동작하지 않는다고 읽습니다.
 *
 * ⚠️ 실패하면 반드시 되돌립니다. 서버에 없는 엔드포인트(배포 전 404)나
 *    ACTED 항목(409)이면 화면만 가려진 채로 남고, 새로고침에 되돌아옵니다.
 *    "가렸습니다" 라고 말해 놓고 되돌아오는 것이 이 기능에서 가장 나쁩니다 —
 *    예전 「넘어가기」가 정확히 그랬습니다.
 *
 * ─────────────────────────────────────────────────────────────
 * `date` 는 캐시 키이자 **서버로 나가는 값**입니다.
 * ─────────────────────────────────────────────────────────────
 * 서버 PATCH 가 `?date=` 를 필수로 받습니다(없으면 400). itemId 가 하루
 * 안에서만 고유해서, 서버가 오늘로 기본값을 두면 지난 리포트를 열어 놓았거나
 * 자정을 넘긴 탭에서 **엉뚱한 날짜의 항목**이 가려지기 때문입니다.
 *
 * 이 훅이 받는 `date` 는 화면이 브리핑을 **조회할 때 쓴 그 날짜**입니다
 * (OpsBriefingTodayPage 의 dateKey — useOpsBriefingQuery 와 같은 값).
 * 그래서 낙관 반영이 건드리는 캐시와 서버가 고치는 문서가 반드시 같은 날짜입니다.
 *
 * ⚠️ 여기서 오늘 날짜를 새로 만들어 쓰지 마세요. 그 순간 화면이 보고 있는
 *    날짜와 서버가 고치는 날짜가 갈라지고, 이 인자를 만든 이유가 사라집니다.
 */
export const useSetOpsBriefingItemStatusMutation = (ctx: Ctx, date: DateKst) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            itemId,
            status,
        }: {
            itemId: string;
            status: UpdateBriefingItemStatusRequest['status'];
        }) => setOpsBriefingItemStatusAPI(ctx, date, itemId, status),
        onMutate: async ({ itemId, status }) => {
            const key = opsBriefingKeys.briefing(ctx.centerId, date);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<OpsBriefingResponse>(key);
            if (prev) {
                qc.setQueryData<OpsBriefingResponse>(key, {
                    ...prev,
                    /*
                     * 배열에서 **빼지 않고 상태만 바꿉니다.**
                     * 빼면 완료 기록 API 가 쓰는 index(서버 배열 위치)가 통째로 밀려
                     * 엉뚱한 항목이 완료 처리됩니다.
                     */
                    items: prev.items.map((it) =>
                        it.itemId === itemId ? { ...it, status } : it
                    ),
                });
            }
            return { prev };
        },
        onError: (_e, _v, context) => {
            const key = opsBriefingKeys.briefing(ctx.centerId, date);
            if (context?.prev) qc.setQueryData(key, context.prev);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: opsBriefingKeys.briefing(ctx.centerId, date) });
        },
    });
};

/* ── ⑥ 지난 리포트 ───────────────────────────────────────── */

export const useOpsHistoryQuery = (ctx: Ctx, month: string, enabled = true) =>
    useQuery<HistoryResponse>({
        queryKey: opsBriefingKeys.history(ctx.centerId, month),
        queryFn: () => getOpsHistoryAPI(ctx, month),
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });

export const useSetHistoryItemDoneMutation = (ctx: Ctx, month: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ date, index, done }: { date: DateKst; index: number; done: boolean }) =>
            setHistoryItemDoneAPI(ctx, date, index, done),
        onMutate: async ({ date, index, done }) => {
            const key = opsBriefingKeys.history(ctx.centerId, month);
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<HistoryResponse>(key);
            if (prev) {
                qc.setQueryData<HistoryResponse>(key, {
                    ...prev,
                    days: prev.days.map((d) => {
                        if (d.date !== date) return d;
                        const highlights = d.highlights.map((h, i) =>
                            i === index ? { ...h, done } : h
                        );
                        // 실행 건수도 같이 움직여야 히트맵 색이 바로 바뀐다
                        return { ...d, highlights, acted: highlights.filter((h) => h.done).length };
                    }),
                });
            }
            return { prev };
        },
        onError: (_e, _v, context) => {
            const key = opsBriefingKeys.history(ctx.centerId, month);
            if (context?.prev) qc.setQueryData(key, context.prev);
        },
        /*
          🔴 **오늘의 브리핑도 함께 무효화한다** (2026-08-31 QA16-ⓐ).

             완료 기록 엔드포인트는 두 화면이 같다
             (`PATCH .../history/:date/items/:index`). 그래서 지난 리포트에서
             체크하면 **서버 상태는 오늘 화면 것도 같이 바뀐다.** 그런데 여기서
             history 캐시만 되살리면, 오늘 화면은 낡은 캐시를 그대로 들고 있다.

             그 결과가 「지난 리포트 5/5, 오늘 4/5」다. 서버를 고쳐도 이 캐시
             때문에 그대로 재현된다 — 원장은 어느 쪽이 맞는지 알 수 없고,
             둘 다 안 믿게 된다.

          ⚠️ 날짜를 특정해서 무효화한다. `date` 는 지난 리포트에서 고른 날이고,
             오늘 화면이 그 날짜를 보고 있을 때만 실제로 다시 받는다.
             'today' 전체를 털면 안 보고 있는 날짜까지 재요청한다.
        */
        onSettled: (_d, _e, variables) => {
            qc.invalidateQueries({ queryKey: opsBriefingKeys.history(ctx.centerId, month) });
            qc.invalidateQueries({
                queryKey: opsBriefingKeys.briefing(ctx.centerId, variables.date),
            });
        },
    });
};

/* ── ⑦ 세팅 집계 2종 (백엔드 3차 B5·B8) ──────────────────── */

/**
 * 🔴 이 두 훅은 **실패해도 `isError` 가 되지 않습니다.**
 *
 * API 계층(`api/opsBriefing.optional`)이 실패를 `null` 로 바꿉니다.
 * 백엔드보다 웹이 먼저 배포될 수 있어서, 서버에 라우트가 없을 때
 * 「불러오지 못했습니다」가 뜨면 **멀쩡하던 브리핑이 고장 나 보입니다** —
 * 있지도 않던 카드 때문에요. 없으면 카드를 안 그리는 것이 맞는 동작입니다.
 *
 * 그래서 호출부는 `isError` 를 보지 말고 **`data == null` 이면 안 그리면** 됩니다.
 * (재시도도 하지 않습니다 — 어차피 삼킬 실패를 두 번 부를 이유가 없습니다)
 *
 * ⚠️ 이 패턴을 다른 조회에 복사하지 마세요. 회원 현황·브리핑 본문이 조용히 비면
 *    원장님은 「오늘은 챙길 일이 없구나」로 읽습니다 — 그건 거짓말입니다.
 */
export const useOpsOnboardingQuery = (ctx: Ctx, enabled = true) =>
    useQuery<OnboardingPayload | null>({
        queryKey: opsBriefingKeys.onboarding(ctx.centerId),
        queryFn: () => getOpsOnboardingAPI(ctx),
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: false,
    });

/**
 * 강사별 온보딩 집계 — 신규·과도기 화면의 원천 (2026-09-01).
 *
 * 🔴 **없으면 `null` 이고 그게 정상이다.** 백엔드가 병렬 구현 중이라
 *    지금은 404 가 온다. 부르는 쪽은 null 을 「이 화면 못 그림」으로 읽고
 *    기존 축 카드로 떨어진다 (`OpsBriefingTodayPage` 의 폴백).
 */
export const useOpsCoachOnboardingQuery = (ctx: Ctx, enabled = true) => {
    const qc = useQueryClient();
    const queryKey = opsBriefingKeys.coachOnboarding(ctx.centerId);
    return useQuery<CoachOnboardingBriefing | null>({
        queryKey,
        /*
         * 🔴 **한 번 받은 카드 묶음은 다시 읽기가 실패해도 지우지 않는다.**
         *
         * API 래퍼(`optional`)는 404 뿐 아니라 500·네트워크 단절도 null 로 삼킨다 —
         * 처음 열 때는 그게 맞다(없으면 없는 대로 기존 화면). 그런데 처리 표시를
         * 저장한 뒤 이력을 다시 읽는 길(`refetch`)에서 그 null 이 그대로 캐시에 들어가면,
         * 원장님이 체크 한 번 눌렀는데 **강사 카드가 통째로 사라진다.**
         * 이미 카드가 있는데 null 이 오면 실패로 던진다 — react-query 는 실패해도 직전
         * data 를 유지하고 status 만 'error' 로 바꾼다. 부르는 쪽은 그 status 를 보고
         * 임시 값을 남길지 정한다(`OpsBriefingTodayPage.refreshCoachOnboarding`).
         */
        queryFn: async () => {
            const next = await getOpsCoachOnboardingAPI(ctx);
            if (next == null && qc.getQueryData<CoachOnboardingBriefing | null>(queryKey) != null) {
                throw new Error('coach-onboarding 다시 읽기 실패 — 직전 카드를 유지합니다');
            }
            return next;
        },
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: false,
    });
};

export const useOpsCoachSettlingQuery = (ctx: Ctx, enabled = true) =>
    useQuery<CoachSettlingPayload | null>({
        queryKey: opsBriefingKeys.coachSettling(ctx.centerId),
        queryFn: () => getOpsCoachSettlingAPI(ctx),
        enabled: ready(ctx, enabled),
        staleTime: DAILY_STALE_MS,
        retry: false,
    });
